import { ICollabCursor, ICollabParticipant } from "../../../services/collab/collab-types";
import "./workflow-collab-overlay.scss";

/**
 * Presence rendering for the BPMN canvas (Faz 5). The screen canvas gets its remote
 * selections/cursors via PostMessage into the iframe agent; the BPMN canvas is an
 * in-window SVG, so this module renders them directly:
 *
 *  - remote SELECTIONS ride the diagram-js `overlays` service (they pan/zoom with the
 *    canvas natively, exactly like the lint overlays);
 *  - remote CURSORS live on an absolutely-positioned DOM layer over the container,
 *    reprojected from diagram coordinates on every `canvas.viewbox.changed`;
 *  - the LOCAL cursor is captured on the container (throttled) in diagram coordinates
 *    so every peer renders it viewport-independently.
 *
 * Decoration-only by contract: pointer-events none everywhere, never touches bpmn-js
 * selection state, and every render survives a missing element (racing deletes) by
 * skipping quietly.
 */

const CURSOR_SEND_THROTTLE_MS: number = 100;
const CURSOR_FADE_MS: number = 3000;
const OVERLAY_TYPE: string = "kuika-wf-remote-selection";

export interface IWfViewbox {
  x: number;
  y: number;
  scale: number;
}

export interface IWfScreenPoint {
  left: number;
  top: number;
}

/** Diagram → container-local CSS coordinates under the current viewbox. */
export function wfDiagramToScreen(viewbox: IWfViewbox, point: ICollabCursor): IWfScreenPoint {
  return {
    left: (point.x - viewbox.x) * viewbox.scale,
    top: (point.y - viewbox.y) * viewbox.scale
  };
}

/** Container-local CSS → diagram coordinates under the current viewbox. */
export function wfScreenToDiagram(viewbox: IWfViewbox, left: number, top: number): ICollabCursor {
  return {
    x: viewbox.x + left / viewbox.scale,
    y: viewbox.y + top / viewbox.scale
  };
}

// ---- selection overlay planning (pure — pinned by tests) ---------------------------------

export interface IWfSelectionOverlayKeyParts {
  clientId: string;
  elementId: string;
  color: string;
  userName: string;
  /** Name chip renders only on the participant's FIRST selected element. */
  withChip: boolean;
}

export interface IWfSelectionOverlayPlan {
  /** Overlay keys ("clientId|elementId|color") no longer wanted — remove their overlay ids. */
  toRemove: string[];
  toAdd: IWfSelectionOverlayKeyParts[];
}

export function wfSelectionOverlayKey(parts: IWfSelectionOverlayKeyParts): string {
  return `${parts.clientId}|${parts.elementId}|${parts.color}`;
}

/**
 * Diffs the currently-rendered overlay keys against the roster's selections. Own client
 * excluded (the local selection marker is bpmn-js's own). A color change (palette reuse
 * after rejoin) re-keys the overlay, so it re-renders in the new color.
 */
export function planWfSelectionOverlays(
  currentKeys: readonly string[],
  roster: readonly ICollabParticipant[],
  ownClientId: string
): IWfSelectionOverlayPlan {
  const wanted: Map<string, IWfSelectionOverlayKeyParts> = new Map<string, IWfSelectionOverlayKeyParts>();
  for (const participant of roster) {
    if (participant.clientId === ownClientId) continue;
    const selected: string[] = participant.selectedIds ?? [];
    selected.forEach((elementId: string, index: number) => {
      const parts: IWfSelectionOverlayKeyParts = {
        clientId: participant.clientId,
        elementId,
        color: participant.color,
        userName: participant.userName,
        withChip: index === 0
      };
      wanted.set(wfSelectionOverlayKey(parts), parts);
    });
  }

  const current: Set<string> = new Set<string>(currentKeys);
  const toRemove: string[] = currentKeys.filter((key: string) => !wanted.has(key));
  const toAdd: IWfSelectionOverlayKeyParts[] = [];
  for (const [key, parts] of wanted) {
    if (!current.has(key)) toAdd.push(parts);
  }
  return { toRemove, toAdd };
}

// ---- live install ---------------------------------------------------------------------------

interface IWfOverlaysService {
  add(elementId: string, type: string, definition: IWfOverlayDefinition): string;
  remove(overlayId: string): void;
}

interface IWfOverlayDefinition {
  position: { top: number; left: number };
  html: HTMLElement;
}

interface IWfCanvasService {
  viewbox(): IWfViewbox;
}

interface IWfEventBusService {
  on(eventName: string, handler: () => void): void;
  off(eventName: string, handler: () => void): void;
}

interface IWfOverlayRegistryElement {
  id: string;
  width?: number;
  height?: number;
}

interface IWfOverlayElementRegistry {
  get(id: string): IWfOverlayRegistryElement | undefined;
}

interface IWfOverlayModeler {
  get(service: "overlays"): IWfOverlaysService;
  get(service: "canvas"): IWfCanvasService;
  get(service: "eventBus"): IWfEventBusService;
  get(service: "elementRegistry"): IWfOverlayElementRegistry;
}

export interface IWfCollabOverlayOptions {
  ownClientId: string;
  onCursorMoved: (x: number, y: number) => void;
}

export interface IWfCollabOverlayHandle {
  updateRoster(roster: readonly ICollabParticipant[]): void;
  dispose(): void;
}

interface IWfCursorEntry {
  element: HTMLDivElement;
  point: ICollabCursor;
  fadeTimer: ReturnType<typeof setTimeout> | null;
}

export function installWorkflowCollabOverlay(
  modeler: unknown,
  container: HTMLElement,
  options: IWfCollabOverlayOptions
): IWfCollabOverlayHandle {
  const typedModeler: IWfOverlayModeler = modeler as IWfOverlayModeler;
  const overlays: IWfOverlaysService = typedModeler.get("overlays");
  const canvas: IWfCanvasService = typedModeler.get("canvas");
  const eventBus: IWfEventBusService = typedModeler.get("eventBus");
  const elementRegistry: IWfOverlayElementRegistry = typedModeler.get("elementRegistry");

  // Cursor layer — over the canvas, under nothing interactive.
  const cursorLayer: HTMLDivElement = document.createElement("div");
  cursorLayer.className = "kuika-wf-collab-cursor-layer";
  container.appendChild(cursorLayer);

  const selectionOverlayIds: Map<string, string> = new Map<string, string>();
  const cursors: Map<string, IWfCursorEntry> = new Map<string, IWfCursorEntry>();
  let disposed: boolean = false;

  const repositionCursors = (): void => {
    if (disposed) return;
    const viewbox: IWfViewbox = canvas.viewbox();
    for (const entry of cursors.values()) {
      const screen: IWfScreenPoint = wfDiagramToScreen(viewbox, entry.point);
      entry.element.style.transform = `translate(${screen.left}px, ${screen.top}px)`;
    }
  };

  const removeCursor = (clientId: string): void => {
    const entry: IWfCursorEntry | undefined = cursors.get(clientId);
    if (!entry) return;
    if (entry.fadeTimer) clearTimeout(entry.fadeTimer);
    entry.element.remove();
    cursors.delete(clientId);
  };

  const upsertCursor = (participant: ICollabParticipant): void => {
    const point: ICollabCursor | null | undefined = participant.cursor;
    if (!point) {
      removeCursor(participant.clientId);
      return;
    }
    let entry: IWfCursorEntry | undefined = cursors.get(participant.clientId);
    if (!entry) {
      const element: HTMLDivElement = document.createElement("div");
      element.className = "kuika-wf-collab-cursor";
      element.innerHTML =
        `<svg width="14" height="18" viewBox="0 0 14 18"><path d="M1 1 L13 8.5 L7.5 10 L5 17 Z" fill="${participant.color}" stroke="#fff" stroke-width="1"/></svg>` +
        `<span class="kuika-wf-collab-cursor__name" style="background-color:${participant.color}"></span>`;
      const nameChip: HTMLSpanElement | null = element.querySelector(".kuika-wf-collab-cursor__name");
      if (nameChip) nameChip.textContent = participant.userName;
      cursorLayer.appendChild(element);
      entry = { element, point, fadeTimer: null };
      cursors.set(participant.clientId, entry);
    }
    entry.point = point;
    entry.element.style.opacity = "1";
    if (entry.fadeTimer) clearTimeout(entry.fadeTimer);
    entry.fadeTimer = setTimeout((): void => {
      entry!.element.style.opacity = "0";
    }, CURSOR_FADE_MS);
  };

  const buildSelectionHtml = (parts: IWfSelectionOverlayKeyParts, element: IWfOverlayRegistryElement): HTMLElement => {
    const wrapper: HTMLDivElement = document.createElement("div");
    wrapper.className = "kuika-wf-collab-selection";
    wrapper.style.outlineColor = parts.color;
    // diagram-js overlays don't auto-size their html — cover the element explicitly
    // (+8px for the -4px offsets on both axes).
    wrapper.style.width = `${(element.width ?? 0) + 8}px`;
    wrapper.style.height = `${(element.height ?? 0) + 8}px`;
    if (parts.withChip) {
      const chip: HTMLSpanElement = document.createElement("span");
      chip.className = "kuika-wf-collab-selection__chip";
      chip.style.backgroundColor = parts.color;
      chip.textContent = parts.userName;
      wrapper.appendChild(chip);
    }
    return wrapper;
  };

  const updateRoster = (roster: readonly ICollabParticipant[]): void => {
    if (disposed) return;

    const plan: IWfSelectionOverlayPlan = planWfSelectionOverlays(
      [...selectionOverlayIds.keys()],
      roster,
      options.ownClientId
    );
    for (const key of plan.toRemove) {
      const overlayId: string | undefined = selectionOverlayIds.get(key);
      selectionOverlayIds.delete(key);
      if (overlayId !== undefined) {
        try {
          overlays.remove(overlayId);
        } catch {
          // Element vanished with its overlay — already gone.
        }
      }
    }
    for (const parts of plan.toAdd) {
      const element: IWfOverlayRegistryElement | undefined = elementRegistry.get(parts.elementId);
      if (!element) continue; // racing a local delete
      try {
        const overlayId: string = overlays.add(parts.elementId, OVERLAY_TYPE, {
          position: { top: -4, left: -4 },
          html: buildSelectionHtml(parts, element)
        });
        selectionOverlayIds.set(wfSelectionOverlayKey(parts), overlayId);
      } catch {
        // Overlay add can throw mid-teardown — decoration only, skip.
      }
    }

    const seen: Set<string> = new Set<string>();
    for (const participant of roster) {
      if (participant.clientId === options.ownClientId) continue;
      seen.add(participant.clientId);
      upsertCursor(participant);
    }
    for (const clientId of [...cursors.keys()]) {
      if (!seen.has(clientId)) removeCursor(clientId);
    }
    repositionCursors();
  };

  // Local cursor capture — throttled, diagram coordinates.
  let lastSentAt: number = 0;
  const handleMouseMove = (event: MouseEvent): void => {
    const now: number = Date.now();
    if (now - lastSentAt < CURSOR_SEND_THROTTLE_MS) return;
    lastSentAt = now;
    const rect: DOMRect = container.getBoundingClientRect();
    const point: ICollabCursor = wfScreenToDiagram(
      canvas.viewbox(),
      event.clientX - rect.left,
      event.clientY - rect.top
    );
    options.onCursorMoved(point.x, point.y);
  };

  container.addEventListener("mousemove", handleMouseMove);
  eventBus.on("canvas.viewbox.changed", repositionCursors);

  return {
    updateRoster,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      container.removeEventListener("mousemove", handleMouseMove);
      try {
        eventBus.off("canvas.viewbox.changed", repositionCursors);
      } catch {
        // Modeler may already be destroyed — its listeners died with it.
      }
      for (const overlayId of selectionOverlayIds.values()) {
        try {
          overlays.remove(overlayId);
        } catch {
          // Destroyed with the modeler.
        }
      }
      selectionOverlayIds.clear();
      for (const clientId of [...cursors.keys()]) {
        removeCursor(clientId);
      }
      cursorLayer.remove();
    }
  };
}
